import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { OPTIONS_CARTE, ajouterFondDeCarte } from './fondDeCarte'

/** Centre de Bamako : point de départ quand l'établissement n'a pas encore de position. */
const BAMAKO: [number, number] = [12.6392, -8.0029]

/**
 * Poser une épingle sur la carte : un clic la place, on peut ensuite la
 * glisser. Même fond de carte (OpenFreeMap) que le suivi en direct.
 *
 * ⚠️ `L.divIcon` et non l'icône par défaut de Leaflet : celle-ci charge des
 * images par un chemin relatif que le bundler ne sert pas — l'épingle serait
 * invisible tout en restant cliquable.
 */
export function ChoixPosition({
  position,
  surChoix,
  hauteur = 220,
}: {
  position: [number, number] | null
  surChoix: (lat: number, lng: number) => void
  hauteur?: number
}) {
  const hote = useRef<HTMLDivElement>(null)
  const carte = useRef<L.Map | null>(null)
  const epingle = useRef<L.Marker | null>(null)
  const rappel = useRef(surChoix)
  rappel.current = surChoix

  useEffect(() => {
    if (!hote.current || carte.current) return
    const m = L.map(hote.current, OPTIONS_CARTE).setView(
      position ?? BAMAKO,
      position ? 16 : 12,
    )
    // Fond OpenFreeMap (tuiles vectorielles, sans clé) — voir `fondDeCarte.ts`.
    ajouterFondDeCarte(m)

    const icone = L.divIcon({
      className: 'epingle-retrait',
      html: '<span></span>',
      iconSize: [26, 26],
      iconAnchor: [13, 26],
    })
    const poser = (lat: number, lng: number) => {
      if (!epingle.current) {
        epingle.current = L.marker([lat, lng], { icon: icone, draggable: true }).addTo(m)
        epingle.current.on('dragend', () => {
          const p = epingle.current!.getLatLng()
          rappel.current(p.lat, p.lng)
        })
      } else {
        epingle.current.setLatLng([lat, lng])
      }
    }
    if (position) poser(position[0], position[1])
    m.on('click', (e: L.LeafletMouseEvent) => {
      poser(e.latlng.lat, e.latlng.lng)
      rappel.current(e.latlng.lat, e.latlng.lng)
    })
    carte.current = m
    // Le panneau latéral s'ouvre en glissant : la carte mesure sa taille trop
    // tôt et n'afficherait qu'un coin de tuiles sans ce recalcul.
    setTimeout(() => m.invalidateSize(), 250)
    return () => {
      m.remove()
      carte.current = null
      epingle.current = null
    }
    // La carte est créée une fois ; l'épingle suit ensuite les clics.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      ref={hote}
      style={{
        height: hauteur,
        borderRadius: 'var(--r2)',
        overflow: 'hidden',
        border: '1px solid var(--bord)',
      }}
    />
  )
}
